import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Inbox, Smartphone } from "lucide-react";
import { Sigil } from "./Sigil";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToId } from "@/lib/scroll";
import { useAuth } from "@/lib/auth";
import { InitiateMark } from "./Seal";
import MoonBanner from "./MoonBanner";

const links = [
  ["gates", "The Gates"],
  ["matrix", "The Matrix"],
  ["vessels", "Vessels"],
  ["books", "The Books"],
  ["ariel", "Goddess Ariel"],
  ["journey", "My Journey"],
  ["initiate", "Initiation"],
  ["apps", "The Apps"],
];

const SoundToggle = () => {
  const ctx = useRef(null);
  const [on, setOn] = useState(false);
  const toggle = () => {
    if (on) {
      ctx.current?.close();
      ctx.current = null;
      setOn(false);
      return;
    }
    const ac = new (window.AudioContext || window.webkitAudioContext)();
    const master = ac.createGain();
    master.gain.setValueAtTime(0, ac.currentTime);
    master.gain.linearRampToValueAtTime(0.05, ac.currentTime + 3);
    master.connect(ac.destination);
    [136.1, 204.15, 272.2].forEach((f, i) => {
      const o = ac.createOscillator();
      const g = ac.createGain();
      const lfo = ac.createOscillator();
      const lg = ac.createGain();
      o.frequency.value = f;
      g.gain.value = 0.5 / (i + 1);
      lfo.frequency.value = 0.08 + i * 0.05;
      lg.gain.value = 0.25 / (i + 1);
      lfo.connect(lg).connect(g.gain);
      o.connect(g).connect(master);
      o.start();
      lfo.start();
    });
    ctx.current = ac;
    setOn(true);
  };
  return (
    <button
      data-testid="ambient-sound-toggle"
      onClick={toggle}
      aria-label={on ? "Silence the hum" : "Play the temple hum"}
      className="flex items-end gap-[3px] h-4 px-1"
    >
      {[0, 1, 2, 3].map((i) => (
        <motion.span
          key={i}
          className="w-[2px] bg-[#D9B47A] rounded-full"
          animate={on ? { height: [4, 14, 6, 12, 4] } : { height: 4 }}
          transition={on ? { repeat: Infinity, duration: 1.4 + i * 0.2, ease: "easeInOut" } : { duration: 0.3 }}
        />
      ))}
    </button>
  );
};

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuTop, setMenuTop] = useState(64);
  const menuRef = useRef(null);
  useLayoutEffect(() => {
    if (open && menuRef.current) setMenuTop(Math.round(menuRef.current.getBoundingClientRect().top));
  }, [open]);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const go = (id) => {
    setOpen(false);
    if (id === "journey" || id === "initiate" || id === "signin" || id === "inbox" || id === "apps") navigate(`/${id}`);
    else if (pathname !== "/") navigate(id === "top" ? "/" : `/#${id}`);
    else scrollToId(id);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled ? "bg-[#0A0706]/75 backdrop-blur-xl border-[#D9B47A]/15" : "bg-transparent border-transparent"
      }`}
    >
      <MoonBanner />
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10 h-16 sm:h-20 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => go("top")} className="flex items-center gap-3 group">
          <Sigil className="w-8 h-8 transition-transform duration-700 group-hover:rotate-[360deg]" width={3.5} />
          <span className="font-display text-lg tracking-wide whitespace-nowrap">
            The Tantric <em className="text-[#D9B47A]">Temple</em>
          </span>
        </button>
        <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
          {links.filter(([id]) => !["matrix", "vessels"].includes(id)).map(([id, label]) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => go(id)}
              className={`relative inline-flex items-center gap-1.5 whitespace-nowrap font-tt-mono text-[12px] uppercase tracking-[0.18em] ${id === "apps" ? "text-[#D9B47A]" : "text-[#CFC2B6]"} hover:text-[#F2E8DF] transition-colors after:absolute after:left-0 after:-bottom-1 after:h-px after:w-full after:bg-[#D9B47A] after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform after:duration-500`}
            >
              {id === "apps" && <Smartphone className="w-3.5 h-3.5" />}
              {label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3 sm:gap-5 xl:gap-4 2xl:gap-5">
          <SoundToggle />
          {user?.role === "admin" && (
            <button data-testid="nav-admin-inbox" onClick={() => go("inbox")} className="hidden md:inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-[#D9B47A] px-4 py-2 font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#0A0706] hover:bg-[#F2E8DF] transition-colors">
              <Inbox className="w-3.5 h-3.5" /> Admin
            </button>
          )}
          {user ? (
            <button data-testid="nav-signout" onClick={logout} className="hidden md:inline-flex items-center gap-3 whitespace-nowrap font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#A8998C] hover:text-[#D9B47A]" title={user.email}>
              {user.initiate && <InitiateMark className="hidden min-[1700px]:inline-flex" />}Sign out
            </button>
          ) : (
            <button data-testid="nav-signin" onClick={() => go("signin")} className="hidden md:inline whitespace-nowrap font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#CFC2B6] hover:text-[#D9B47A]">
              Sign in
            </button>
          )}
          <button
            data-testid="nav-cta-enter"
            onClick={() => go("enter")}
            className={`${user ? "hidden sm:inline-flex xl:hidden min-[1700px]:inline-flex" : "hidden sm:inline-flex"} whitespace-nowrap rounded-full border border-[#D9B47A]/60 px-5 py-2.5 font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#F2E8DF] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors duration-300`}
          >
            Enter the Temple
          </button>
          <button data-testid="nav-mobile-toggle" className="xl:hidden" onClick={() => setOpen((v) => !v)} aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            ref={menuRef}
            style={{ maxHeight: `calc(100dvh - ${menuTop}px)` }}
            data-lenis-prevent
            className="xl:hidden overflow-y-auto overscroll-contain bg-[#0A0706]/95 backdrop-blur-xl border-b border-[#D9B47A]/15 px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-2 flex flex-col gap-5"
          >
            {user?.initiate && <InitiateMark className="self-start mt-3" />}
            {[...(user?.role === "admin" ? [["inbox", "Admin Inbox"]] : []), ...links, ["enter", "Enter the Temple"], user ? ["signout", "Sign out"] : ["signin", "Sign in"]].map(([id, label]) => (
              <button key={id} data-testid={`nav-mobile-link-${id}`} onClick={() => (id === "signout" ? (setOpen(false), logout()) : go(id))} className={`text-left font-display text-3xl ${id === "inbox" || id === "apps" ? "text-[#D9B47A]" : ""}`}>
                {label}
              </button>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
